// shared/weeks.ts
/**
 * Week buckets for dashboard charts. Weeks start Monday in America/Chicago:
 * the container runs UTC, and a Sunday-evening Central activity must land in
 * that week, not the next. DB timestamps are UTC wall-clock
 * `timestamp without time zone`, so boundaries are handed to SQL as UTC ISO
 * strings (Postgres drops the trailing Z when casting to timestamp).
 */
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

export const CENTRAL_TZ = "America/Chicago";

/** Calendar arithmetic on a YYYY-MM-DD key; no time zone involved. */
export function addDaysKey(key: string, days: number): string {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Today's date key (YYYY-MM-DD) in tz. The container runs UTC, so never slice an ISO string for this. */
export function todayKey(now: Date, tz: string = CENTRAL_TZ): string {
  return formatInTimeZone(now, tz, "yyyy-MM-dd");
}

/** Monday keys for the last n weeks in tz, oldest first, current week last. */
export function lastNWeekStarts(now: Date, n: number, tz: string = CENTRAL_TZ): string[] {
  const today = formatInTimeZone(now, tz, "yyyy-MM-dd");
  const isoDow = Number(formatInTimeZone(now, tz, "i")); // 1 = Monday, 7 = Sunday
  const monday = addDaysKey(today, -(isoDow - 1));
  return Array.from({ length: n }, (_, i) => addDaysKey(monday, -7 * (n - 1 - i)));
}

/** UTC instant (ISO, with Z) of 00:00 in tz on that date. */
export function tzMidnightUtc(key: string, tz: string = CENTRAL_TZ): string {
  return fromZonedTime(`${key}T00:00:00`, tz).toISOString();
}

export function fillWeeks(keys: string[], rows: { wk: string; n: number }[]): number[] {
  const byWeek = new Map<string, number>();
  for (const r of rows) byWeek.set(r.wk, (byWeek.get(r.wk) ?? 0) + r.n);
  return keys.map((k) => byWeek.get(k) ?? 0);
}

export function weekLabel(key: string): string {
  return new Date(`${key}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** Monday key (YYYY-MM-DD) of the tz week holding this instant. */
export function weekStartKey(iso: string, tz: string = CENTRAL_TZ): string {
  const d = new Date(iso);
  const day = formatInTimeZone(d, tz, "yyyy-MM-dd");
  const isoDow = Number(formatInTimeZone(d, tz, "i"));
  return addDaysKey(day, -(isoDow - 1));
}
